import React, { useState, useEffect } from 'react';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';
import { db } from '../services/db';
import { Save, Loader2, Network } from 'lucide-react';
import { toast } from 'sonner';

import { M8_MetaAds } from './modules/M8_MetaAds';
import { M9_Google } from './modules/M9_Google';
import { M10_WhatsApp } from './modules/M10_WhatsApp';
import { M11_CRM } from './modules/M11_CRM';
import { SmartCrmBoard } from './SmartCrmBoard';
import { UtmGeneratorBoard } from './UtmGeneratorBoard';

interface TrackingIntegrationsProps {
  client: ClientEntity;
}

export const TrackingIntegrations: React.FC<TrackingIntegrationsProps> = ({ client }) => {
  const [activeTab, setActiveTab] = useState<'meta' | 'google' | 'whatsapp' | 'crm' | 'utm'>('meta');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const store = useStore();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await db.getClientBusinessBrain(client.id);
        if (data) {
          // Apenas carregamos o que é usado nessas tabs
          if (data.crm_data) store.setCrmData(data.crm_data);
          if (data.connections) store.setConnections(data.connections);
        }
      } catch (err) {
        console.error('Error loading business brain:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [client.id, store]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await db.saveClientBusinessBrain(client.id, store);
      toast.success('Integrações salvas com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar as integrações.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <Network size={24} className="text-black" /> Integrações & Tracking
          </h2>
          <p className="text-gray-500 mt-1">
            Conecte as ferramentas e garanta que a IA veja toda a jornada, do clique à venda.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-black hover:bg-gray-900 disabled:bg-gray-400 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm shrink-0"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isSaving ? 'Salvando...' : 'Salvar Conexões'}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200 hide-scrollbar bg-gray-50/50">
          <button
            onClick={() => setActiveTab('meta')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'meta' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Meta Ads & Pixel
            {activeTab === 'meta' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('google')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'google' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Google Ads & GA4
            {activeTab === 'google' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'whatsapp' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            WhatsApp Business
            {activeTab === 'whatsapp' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('crm')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'crm' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            CRM & Vendas
            {activeTab === 'crm' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>
          <button
            onClick={() => setActiveTab('utm')}
            className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
              activeTab === 'utm' ? 'text-black bg-white' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            Gerador UTM (X9)
            {activeTab === 'utm' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />}
          </button>

        </div>
        
        <div className="p-6 md:p-8">
          {activeTab === 'meta' && <M8_MetaAds />}
          {activeTab === 'google' && <M9_Google />}
          {activeTab === 'whatsapp' && <M10_WhatsApp />}
          {activeTab === 'crm' && <SmartCrmBoard />}

          {activeTab === 'utm' && <UtmGeneratorBoard client={client} />}

        </div>
      </div>
    </div>
  );
};
